import { BookMode, BookType, BookWithProgress } from '../Book'
import * as JourneyTypes from '../Journey'
import { LearningJourneyEntityType } from '../Journey'
import { selectLibraryContent } from './Library.selectors'
import {
  LabelProps,
  LabelType,
  Shelf,
  ShelfEntry,
  ShelfType,
  StoreRecommendedItem,
  StoreShelf,
  StoreShelfEntry,
  StoreShelfSection
} from './Library.types'

export const notNull = <TValue>(value: TValue | null): value is TValue => value !== null

export const getBookMode = ({
  bookType,
  shelfType,
  shelfSectionType
}: {
  bookType?: BookType
  shelfType?: ShelfType
  shelfSectionType?: ShelfType
}) => {
  return bookType && bookType !== BookType.Mix
    ? bookType === BookType.Reading
      ? BookMode.READ
      : BookMode.LISTEN
    : (shelfSectionType || shelfType) === ShelfType.LISTENING
    ? BookMode.LISTEN
    : BookMode.READ
}

export const getShelfTitleWithElementsCount = (shelf: Shelf) => {
  if (!shelf.title) return

  const shelfSize = shelf.itemsTotal !== undefined ? `(${shelf.itemsTotal})` : ''

  return `${shelf.title} ${shelfSize}`
}

export const getLibraryItemName = (
  item: StoreShelfEntry | StoreRecommendedItem,
  { isRecommended }: { isRecommended?: boolean } = {}
) => {
  if (!item) return 'unknown'

  const namePrefix = isRecommended ? 'recommended ' : ''
  let name = `${namePrefix}${item.type} - `

  switch (item.type) {
    case LearningJourneyEntityType.Book:
    case LearningJourneyEntityType.StudySet:
    case LearningJourneyEntityType.TutorVideoMessage:
      name += item.data.title
      break
    case LearningJourneyEntityType.PowerUp:
      name += 'routine' in item.data ? item.data.routine.title : item.data.title
      break
    case LearningJourneyEntityType.LevelUp:
      name += item.data.subtype
      break
  }

  return name
}

export function flatMap<T, R>(array: T[], mapper: (item: T, index: number, array: T[]) => R | R[]): R[] {
  const result: R[] = []

  for (let i = 0; i < array.length; i++) {
    const mapped = mapper(array[i], i, array)
    if (Array.isArray(mapped)) {
      result.push(...mapped)
    } else {
      result.push(mapped)
    }
  }

  return result
}

export const getShelvesImagesToPreload = (shelves: ReturnType<typeof selectLibraryContent>['shelves']) =>
  flatMap(shelves, (shelve) =>
    flatMap(shelve.sections, (section) =>
      section.entries.map((entry) => {
        switch (entry.type) {
          case LearningJourneyEntityType.Book:
            return entry.data.book.imageUrl
          case LearningJourneyEntityType.PowerUp:
          case LearningJourneyEntityType.StudySet:
            return entry.data.imageUrl
          case LearningJourneyEntityType.TutorVideoMessage:
            return entry.data.tutorProfile.avatarUrl
          default:
            return undefined
        }
      })
    )
  ).filter(Boolean)

export const getProcessedBookShelfEntry = (entry: ShelfEntry, booksWithProgress: BookWithProgress[]) => {
  if (entry.type !== LearningJourneyEntityType.Book) return entry

  const booksWithProgresses = booksWithProgress.filter((item) => item.book._id === entry.data.bookId)

  const bookWithProgressForLibrary = booksWithProgresses.find((item) => !item.bookProgress?.isJourneyPage)

  if (!bookWithProgressForLibrary && !booksWithProgresses[0]) return null

  const bookWithProgress = bookWithProgressForLibrary || { ...booksWithProgresses[0], bookProgress: null }

  const entryBookWithProgress = {
    ...bookWithProgress,
    book: {
      ...bookWithProgress.book,
      bookType: entry.data.type
    }
  }

  return {
    ...entry,
    data: {
      ...entry.data,
      ...entryBookWithProgress
    }
  }
}

export const getProcessedRecommendedItem = (
  recommendedContent: ShelfEntry | null,
  booksWithProgress: BookWithProgress[]
) =>
  recommendedContent?.type === LearningJourneyEntityType.Book
    ? getProcessedBookShelfEntry(recommendedContent, booksWithProgress)
    : recommendedContent || null

export const getProcessedShelves = (shelves: Shelf[], booksWithProgress: BookWithProgress[]) =>
  shelves.map((shelf) => ({
    ...shelf,
    sections: shelf.sections.map((section) => ({
      ...section,
      entries: section.entries
        .map((entry) =>
          entry.type === LearningJourneyEntityType.Book ? getProcessedBookShelfEntry(entry, booksWithProgress) : entry
        )
        .filter(notNull)
    }))
  }))

export const isShelfEmpty = (shelf: StoreShelf) =>
  !shelf.sections.reduce((sum, section) => section.entries.length + sum, 0)

export const getShelfKey = (shelf: StoreShelf | Shelf) => `shelf-${shelf.title || shelf.type}`

export const getShelfSectionKey = (shelfSection: StoreShelfSection) =>
  `section-${shelfSection.title || shelfSection.type}`

export const getActivityLabelFromAttributes = (
  attributes: JourneyTypes.LearningJourneyEntryAttributes,
  { locked, withoutText, large }: { locked?: boolean; withoutText?: boolean; large?: boolean }
): LabelProps | undefined => {
  switch (attributes.coverLabel) {
    case JourneyTypes.LearningJourneyCoverLabel.UNAVAILABLE: {
      return { type: LabelType.PREMIUM_ACTIVITY, withoutText, large }
    }
    case JourneyTypes.LearningJourneyCoverLabel.GIFT: {
      return { type: LabelType.GIFT_ACTIVITY, large }
    }
  }

  if (locked) return { type: LabelType.PREMIUM_ACTIVITY, withoutText, large }

  return undefined
}
